import React, { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Plus, Briefcase, MapPin, DollarSign, Users2, UserCircle2, Rocket, Building2, EyeOff, Shield } from "lucide-react";
import { useLang } from "@/contexts/LanguageContext";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Avatar, AvatarImage, AvatarFallback } from "@/components/ui/avatar";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import FreelancersSection from "@/pages/work/FreelancersSection";
import MyWorkProfile from "@/pages/work/MyWorkProfile";
import PlansSection from "@/pages/work/PlansSection";
import MyJobsSection from "@/pages/work/MyJobsSection";
import SponsoredAdWidget from "@/components/SponsoredAdWidget";
import WorkSearchBar from "@/pages/work/WorkSearchBar";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CATEGORIES = ["Tecnologia", "Design", "Marketing", "Reformas", "Ensino", "Serviços Gerais", "Delivery", "Outros"];

export default function Work() {
  const { t } = useLang();
  const { user } = useAuth();
  const [jobs, setJobs] = useState([]);

  // Deep-link from notifications/push: /work?job=<id> → scroll to and highlight the job card
  useEffect(() => {
    const id = new URLSearchParams(window.location.search).get("job");
    if (!id || !jobs.length) return;
    const el = document.querySelector(`[data-testid="job-${id}"]`);
    if (el) {
      el.scrollIntoView({ behavior: "smooth", block: "center" });
      el.classList.add("ring-2", "ring-primary");
      setTimeout(() => el.classList.remove("ring-2", "ring-primary"), 4000);
    }
  }, [jobs]);
  const [open, setOpen] = useState(false);
  const [selectedJob, setSelectedJob] = useState(null);
  const [applyOpen, setApplyOpen] = useState(false);
  const [form, setForm] = useState({ title: "", description: "", category: "Tecnologia", budget: "", location: "", hourly_rate_brl: "", training_day_paid: false, training_day_transport_brl: "0", training_day_meal_brl: "0" });
  const [apply, setApply] = useState({ message: "", price: 0 });
  const [cfg, setCfg] = useState({
    texts: { heading: "Trabalhos", subheading: "Encontre ou publique trabalhos", create_btn: "Publicar trabalho", empty_state: "Nenhum trabalho ainda. Seja o primeiro!", dialog_title: "Publicar um trabalho", apply_btn: "Candidatar-se", info_banner: "" },
    contractor_fee_pct: 0, worker_fee_pct: 0, goals: [],
  });
  const [progress, setProgress] = useState(null);

  const [nearMe, setNearMe] = useState(() => { try { return localStorage.getItem("pdv-work-near") !== "0"; } catch { return true; } });
  const load = async (near = nearMe) => {
    const res = await axios.get(`${API}/jobs?near=${near ? 1 : 0}`);
    setJobs(res.data);
  };
  const toggleNear = () => {
    const v = !nearMe;
    setNearMe(v);
    try { localStorage.setItem("pdv-work-near", v ? "1" : "0"); } catch { /* ignore */ }
    load(v);
  };
  const nearCount = jobs.filter((j) => j.near).length;
  const userLoc = [user?.city, user?.state].filter(Boolean).join(", ");
  const loadCfg = async () => {
    try {
      const [c, m] = await Promise.all([
        axios.get(`${API}/work/config`),
        axios.get(`${API}/work/me`).catch(() => ({ data: null })),
      ]);
      setCfg(c.data);
      if (m.data) setProgress(m.data);
    } catch (err) {
      console.error("[Work] loadCfg failed", err);
    }
  };
  useEffect(() => { load(); loadCfg(); }, []);

  const claimBonus = async (goalName) => {
    try {
      const res = await axios.post(`${API}/work/claim-bonus`, { goal_name: goalName });
      toast.success(`Bônus de R$ ${res.data.bonus_brl.toFixed(2)} creditado!`);
      loadCfg();
    } catch (e) { toast.error(e.response?.data?.detail || "Erro"); }
  };

  const submitJob = async () => {
    if (!form.title || !form.description) return toast.error("Preencha todos os campos");
    try {
      await axios.post(`${API}/jobs`, {
        ...form,
        budget: Number(form.budget) || 0,
        hourly_rate_brl: Number(form.hourly_rate_brl) || 0,
        remote: true,
        training_day_paid: !!form.training_day_paid,
        training_day_transport_brl: Number(form.training_day_transport_brl || 0),
        training_day_meal_brl: Number(form.training_day_meal_brl || 0),
      });
      toast.success("Trabalho publicado!");
      setOpen(false);
      setForm({ title: "", description: "", category: "Tecnologia", budget: 0, location: "" });
      load();
    } catch { toast.error("Erro"); }
  };

  const submitApply = async () => {
    if (!apply.message || !apply.price) return toast.error("Preencha proposta e preço");
    try {
      await axios.post(`${API}/jobs/${selectedJob.job_id}/apply`, { ...apply, price: Number(apply.price) });
      toast.success(t("applied"));
      setApplyOpen(false);
      setApply({ message: "", price: 0 });
      load();
    } catch { toast.error("Erro"); }
  };

  return (
    <div className="max-w-6xl mx-auto space-y-6">
      <SponsoredAdWidget variant="inline" limit={2} />
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight" data-testid="work-heading">{cfg.texts.heading || t("jobsMarketplace")}</h1>
          <p className="text-muted-foreground mt-1" data-testid="work-subheading">{cfg.texts.subheading}</p>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button data-testid="create-job-btn" className="rounded-full gap-2">
              <Plus className="w-4 h-4" /> {cfg.texts.create_btn || t("createJob")}
            </Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader><DialogTitle>{cfg.texts.dialog_title || t("createJob")}</DialogTitle></DialogHeader>
            <div className="space-y-3">
              <Input data-testid="job-title" placeholder={t("jobTitle")} value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} />
              <Textarea data-testid="job-description" placeholder={t("jobDescription")} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} rows={4} />
              <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}>
                <SelectTrigger data-testid="job-category"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {CATEGORIES.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                </SelectContent>
              </Select>
              <Input data-testid="job-budget" type="number" placeholder={t("jobBudget")} value={form.budget || ""} onChange={(e) => setForm({ ...form, budget: e.target.value })} />
              <div>
                <Input
                  data-testid="job-hourly-rate"
                  type="number"
                  min="0"
                  step="0.5"
                  placeholder="Valor por hora (R$) — opcional para empresas"
                  value={form.hourly_rate_brl || ""}
                  onChange={(e) => setForm({ ...form, hourly_rate_brl: e.target.value })}
                />
                <p className="text-[10px] text-muted-foreground mt-1">Preencha o valor por hora se preferir contratação por hora (aparece na vaga).</p>
              </div>
              {cfg.contractor_fee_pct > 0 && Number(form.budget) > 0 && (
                <div className="text-xs text-muted-foreground p-2 rounded bg-orange-500/10">
                  Taxa do contratante ({cfg.contractor_fee_pct}%): <strong>R$ {(Number(form.budget) * cfg.contractor_fee_pct / 100).toFixed(2)}</strong> · Total a debitar: <strong>R$ {(Number(form.budget) * (1 + cfg.contractor_fee_pct / 100)).toFixed(2)}</strong>
                </div>
              )}
              <Input data-testid="job-location" placeholder={t("jobLocation")} value={form.location} onChange={(e) => setForm({ ...form, location: e.target.value })} />
              <div className="grid grid-cols-2 gap-2">
                <Input data-testid="job-city" placeholder="Cidade" value={form.city || ""} onChange={(e) => setForm({ ...form, city: e.target.value })} />
                <Input data-testid="job-neighborhood" placeholder="Bairro" value={form.neighborhood || ""} onChange={(e) => setForm({ ...form, neighborhood: e.target.value })} />
              </div>
              <div className="p-3 rounded-lg border border-dashed border-primary/40 space-y-2 bg-primary/5" data-testid="job-training-block">
                <label className="flex items-center gap-2 text-sm font-semibold cursor-pointer">
                  <input
                    data-testid="job-training-paid"
                    type="checkbox"
                    checked={form.training_day_paid}
                    onChange={(e) => setForm({ ...form, training_day_paid: e.target.checked })}
                    className="w-4 h-4 accent-primary"
                  />
                  1º dia de treinamento remunerado
                </label>
                {form.training_day_paid && (
                  <div className="grid grid-cols-2 gap-2">
                    <div>
                      <label className="text-[10px] text-muted-foreground">Vale-transporte (R$)</label>
                      <Input data-testid="job-training-transport" type="number" min="0" step="0.5" value={form.training_day_transport_brl} onChange={(e) => setForm({ ...form, training_day_transport_brl: e.target.value })} />
                    </div>
                    <div>
                      <label className="text-[10px] text-muted-foreground">Vale-refeição (R$)</label>
                      <Input data-testid="job-training-meal" type="number" min="0" step="0.5" value={form.training_day_meal_brl} onChange={(e) => setForm({ ...form, training_day_meal_brl: e.target.value })} />
                    </div>
                    <div className="col-span-2 text-[11px] text-muted-foreground">
                      Total do 1º dia: <strong className="text-primary">R$ {(Number(form.training_day_transport_brl || 0) + Number(form.training_day_meal_brl || 0)).toFixed(2)}</strong>
                    </div>
                  </div>
                )}
              </div>
              <Button data-testid="job-submit" onClick={submitJob} className="w-full rounded-full">{t("post")}</Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>

      <WorkSearchBar onJobSelect={(j) => {
        const el = document.querySelector(`[data-testid="job-${j.job_id}"]`);
        if (el) { el.scrollIntoView({ behavior: "smooth", block: "center" }); el.classList.add("ring-2", "ring-primary"); setTimeout(() => el.classList.remove("ring-2", "ring-primary"), 4000); }
        else window.history.replaceState(null, "", `/work?job=${j.job_id}`);
      }} />

      <Tabs defaultValue="jobs">
        <TabsList className="rounded-full flex flex-wrap w-full max-w-3xl gap-1 h-auto p-1">
          <TabsTrigger value="jobs" data-testid="work-tab-jobs" className="rounded-full gap-1 text-xs"><Briefcase className="w-4 h-4" />Vagas</TabsTrigger>
          <TabsTrigger value="myjobs" data-testid="work-tab-myjobs" className="rounded-full gap-1 text-xs"><Shield className="w-4 h-4" />Meus trabalhos</TabsTrigger>
          <TabsTrigger value="freelancers" data-testid="work-tab-freelancers" className="rounded-full gap-1 text-xs"><UserCircle2 className="w-4 h-4" />Freelancers</TabsTrigger>
          <TabsTrigger value="profile" data-testid="work-tab-profile" className="rounded-full gap-1 text-xs"><Building2 className="w-4 h-4" />Meu perfil</TabsTrigger>
          <TabsTrigger value="plans" data-testid="work-tab-plans" className="rounded-full gap-1 text-xs"><Rocket className="w-4 h-4" />Planos</TabsTrigger>
        </TabsList>

        <TabsContent value="jobs" className="mt-4 space-y-4">
          {cfg.texts.info_banner && (
            <Card className="p-3 border-primary/40 bg-primary/5 text-sm" data-testid="work-info-banner">{cfg.texts.info_banner}</Card>
          )}

          {progress && (cfg.goals?.length > 0) && (
            <Card className="p-4" data-testid="work-goals-card">
              <div className="flex items-center justify-between mb-3">
                <div>
                  <div className="text-xs uppercase tracking-widest text-muted-foreground font-semibold">Metas de trabalho</div>
                  <div className="text-sm">Trabalhos concluídos: <strong data-testid="work-jobs-completed">{progress.jobs_completed}</strong></div>
                </div>
                {progress.next_goal && (
                  <div className="text-xs text-muted-foreground">
                    Próxima: <strong>{progress.next_goal.name}</strong> ({progress.jobs_completed}/{progress.next_goal.jobs_target}) → R$ {Number(progress.next_goal.bonus_brl).toFixed(2)}
                  </div>
                )}
              </div>
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-2">
                {cfg.goals.map((g) => {
                  const done = progress.jobs_completed >= (g.jobs_target || 0);
                  const achieved = progress.achieved?.find(a => a.name === g.name);
                  const paid = achieved?.paid;
                  return (
                    <div key={g.name} className={`p-3 rounded-lg border ${done ? "border-green-500/50 bg-green-500/5" : "border-border"}`} data-testid={`work-goal-${g.name}`}>
                      <div className="flex items-center justify-between mb-1">
                        <div className="font-bold text-sm">{g.name}</div>
                        <Badge variant={done ? "default" : "secondary"} className="text-[10px]">{g.jobs_target} trabalhos</Badge>
                      </div>
                      <div className="text-xs text-muted-foreground mb-2">Bônus: <strong className="text-primary">R$ {Number(g.bonus_brl).toFixed(2)}</strong></div>
                      {done && !paid && (
                        <Button size="sm" data-testid={`claim-bonus-${g.name}`} onClick={() => claimBonus(g.name)} className="w-full rounded-full h-7 text-xs">Resgatar bônus</Button>
                      )}
                      {paid && <div className="text-xs text-green-600 font-semibold">✓ Bônus recebido</div>}
                    </div>
                  );
                })}
              </div>
            </Card>
          )}

          <div className="flex items-center justify-between gap-2 flex-wrap" data-testid="work-near-bar">
            <div className="text-xs text-muted-foreground inline-flex items-center gap-1">
              <MapPin className="w-3.5 h-3.5 text-orange-500" />
              {userLoc
                ? (nearMe ? <>Mostrando primeiro vagas perto de <b className="text-foreground">{userLoc}</b>{nearCount ? <> · <b className="text-foreground">{nearCount}</b> perto de você</> : " · nenhuma vaga local ainda, veja as demais abaixo"}</> : <>Ordenando por mais recentes</>)
                : <>Informe sua cidade no <a href="/profile" className="underline">perfil</a> para ver vagas perto de você primeiro</>}
            </div>
            {userLoc && (
              <Button size="sm" variant={nearMe ? "default" : "outline"} className="rounded-full h-7 text-xs gap-1" onClick={toggleNear} data-testid="work-near-toggle">
                <MapPin className="w-3 h-3" /> {nearMe ? "Perto de mim: ativo" : "Perto de mim"}
              </Button>
            )}
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4 stagger">
            {jobs.length === 0 && (
              <Card className="p-12 text-center col-span-full">
                <Briefcase className="w-12 h-12 mx-auto text-muted-foreground/40 mb-4" />
                <p className="text-muted-foreground" data-testid="work-empty">{cfg.texts.empty_state}</p>
              </Card>
            )}
            {jobs.map(j => (
              <Card key={j.job_id} className="p-5 hover-lift animate-pop-in cursor-pointer border-l-4 border-l-orange-500" data-testid={`job-${j.job_id}`}>
                <div className="flex items-start justify-between mb-3">
                  <div className="flex items-center gap-1 flex-wrap">
                    <Badge variant="secondary" className="text-xs">{j.category}</Badge>
                    {j.near && <Badge className={`text-[10px] gap-1 ${j.near === "city" ? "bg-emerald-500" : "bg-cyan-600"} text-white`} data-testid={`job-near-${j.job_id}`}><MapPin className="w-3 h-3" /> {j.near === "city" ? "Na sua cidade" : "No seu estado"}</Badge>}
                  </div>
                  <div className="text-right">
                    <div className="text-lg font-bold text-orange-500">R$ {j.budget}</div>
                    {j.hourly_rate_brl > 0 && (
                      <div className="text-[10px] text-muted-foreground" data-testid={`job-hourly-${j.job_id}`}>
                        R$ {Number(j.hourly_rate_brl).toFixed(2)}/h
                      </div>
                    )}
                  </div>
                </div>
                <h3 className="font-bold text-lg mb-2 leading-tight">{j.title}</h3>
                <p className="text-sm text-muted-foreground line-clamp-3 mb-4">{j.description}</p>
                <div className="flex items-center gap-3 text-xs text-muted-foreground mb-4">
                  {j.location && <span className="flex items-center gap-1"><MapPin className="w-3 h-3" />{j.location}</span>}
                  <span className="flex items-center gap-1"><Users2 className="w-3 h-3" />{j.applications?.length || 0} {t("applications")}</span>
                  {j.training_day_paid && (
                    <Badge variant="outline" className="text-[10px] gap-1" data-testid={`job-training-badge-${j.job_id}`}>
                      1º dia +R$ {(Number(j.training_day_transport_brl || 0) + Number(j.training_day_meal_brl || 0)).toFixed(2)}
                    </Badge>
                  )}
                </div>
                <div className="flex items-center justify-between pt-3 border-t border-border">
                  <div className="flex items-center gap-2">
                    {j.is_company_job && !j.company_visible ? (
                      <>
                        <Avatar className="w-7 h-7">
                          <AvatarFallback className="text-xs bg-muted"><EyeOff className="w-3 h-3" /></AvatarFallback>
                        </Avatar>
                        <span className="text-xs italic text-muted-foreground">Empresa confidencial</span>
                      </>
                    ) : (
                      <>
                        <Avatar className="w-7 h-7">
                          <AvatarImage src={j.author_picture} />
                          <AvatarFallback className="text-xs">{j.author_name?.[0]}</AvatarFallback>
                        </Avatar>
                        <span className="text-xs">{j.author_name}</span>
                      </>
                    )}
                  </div>
                  <Button data-testid={`apply-btn-${j.job_id}`} size="sm" className="rounded-full" onClick={(e) => { e.stopPropagation(); setSelectedJob(j); setApplyOpen(true); }}>
                    {cfg.texts.apply_btn || t("apply")}
                  </Button>
                </div>
              </Card>
            ))}
          </div>
        </TabsContent>

        <TabsContent value="freelancers" className="mt-4"><FreelancersSection /></TabsContent>
        <TabsContent value="myjobs" className="mt-4"><MyJobsSection /></TabsContent>
        <TabsContent value="profile" className="mt-4"><MyWorkProfile /></TabsContent>
        <TabsContent value="plans" className="mt-4"><PlansSection /></TabsContent>
      </Tabs>

      <Dialog open={applyOpen} onOpenChange={setApplyOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>{selectedJob?.title}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <Textarea data-testid="apply-message" placeholder={t("yourProposal")} value={apply.message} onChange={(e) => setApply({ ...apply, message: e.target.value })} rows={4} />
            <Input data-testid="apply-price" type="number" placeholder={t("yourPrice")} value={apply.price || ""} onChange={(e) => setApply({ ...apply, price: e.target.value })} />
            <Button data-testid="apply-submit" onClick={submitApply} className="w-full rounded-full">{t("apply")}</Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
